import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const SECTIONS = [
  {
    n: "01",
    title: "Mandatory 48-Hour Return Reporting Window",
    body:
      "If you receive a wrong, damaged, or defective product, you must inform our team in writing within 48 hours of delivery.",
    outro:
      "Claims submitted after the 48-hour window will not be entertained or processed under any circumstances.",
    list: null,
  },
  {
    n: "02",
    title: "Mandatory Verification & Video Proof",
    body:
      "To have a return or exchange request approved, submitting the following two videos is strictly mandatory.",
    list: [
      [
        "Unboxing Video",
        "An uncut, unedited, continuous recording of the parcel being opened. The courier tracking label must be clearly visible throughout, and the video must show the opening from a sealed state through to the defect on the product.",
      ],
      [
        "Repacking Video",
        "If your return is approved, record a brief video while securely repacking the product for courier pick-up.",
      ],
    ],
    outro:
      "Failure to provide valid unboxing and repacking videos will result in immediate rejection of the return claim.",
  },
  {
    n: "03",
    title: "Mode of Refund — Store Credit / Gift Voucher Only",
    body:
      "We do not issue direct cash refunds or bank transfers under any circumstances.",
    list: [
      [
        "Store Credit",
        "Upon approval of your return and physical quality inspection at our warehouse, you will be issued a Store Credit / Gift Voucher Code equal to the value of the returned item.",
      ],
      [
        "Usage",
        "The store credit can be redeemed on your next purchase on theblackhouse.in.",
      ],
      ["Validity", "6 months from the date of issue."],
    ],
    outro: null,
  },
  {
    n: "04",
    title: "Legal Compliance — Consumer Protection Act, 2019",
    body:
      "Our operational policies comply with Indian statutory regulations to safeguard consumer rights and ensure transparency.",
    list: [
      [
        "Unfair Trade Practices — Sec 2(47)",
        "All product imagery, size specifications (XS to XXL), and descriptions on our platform accurately represent the physical product.",
      ],
      [
        "Defective Goods Redressal",
        "If a delivered item has a manufacturing defect or significant mismatch, the consumer is legally entitled to resolution — provided it is reported within the mandatory 48-hour timeframe.",
      ],
    ],
    outro: null,
  },
  {
    n: "05",
    title: "Consumer Protection (E-Commerce) Rules, 2020",
    body:
      "All return, refund, shipping, and payment terms are explicitly disclosed prior to checkout in line with Rules 5 & 6.",
    list: [
      [
        "Grievance Timeline",
        "Customer complaints are acknowledged within 48 hours of receipt and resolved systematically within one month.",
      ],
      [
        "Cancellation Charges",
        "No arbitrary or unfair cancellation fees are charged to the customer for orders cancelled prior to fulfillment or due to platform errors.",
      ],
    ],
    outro: null,
  },
  {
    n: "06",
    title: "Non-Returnable Conditions",
    body: "Returns will not be accepted under the following scenarios:",
    list: [
      ["A.", "Claims made after 48 hours of delivery."],
      ["B.", "Inability to provide valid unboxing and repacking videos."],
      [
        "C.",
        "Returns requested due to minor colour variations or personal preference. Please review the size chart carefully before ordering.",
      ],
      [
        "D.",
        "Products that show any sign of wear, washing, alteration, or missing original tags and packaging.",
      ],
    ],
    outro: null,
  },
  {
    n: "07",
    title: "How To Submit A Return Request",
    body:
      "Approved returns move through three ordered steps. Please follow each.",
    list: [
      [
        "Step 1 · Contact",
        "Reach us within 48 hours of delivery at support@theblackhouse.in — or via our support line.",
      ],
      [
        "Step 2 · Provide",
        "Share your Order ID, reason for return, and attach the mandatory unboxing video.",
      ],
      [
        "Step 3 · Review",
        "Our team reviews the footage within 24–48 hours and guides you on courier pick-up and the store credit process upon approval.",
      ],
    ],
    outro: null,
  },
];

export default function ReturnsRefunds() {
  useEffect(() => {
    document.title = "Returns & Refunds — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="returns-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Returns
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Last Updated · 20 · VII · MMXXVI
      </motion.div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Returns · Refunds · Legal Compliance
          </p>
          <h1
            data-testid="returns-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            Fair, Transparent
            <br />
            & Final
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-xl mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("The Black House is committed to a transparent, fair, and premium shopping experience. As every drop is limited-inventory, please read this policy carefully before placing an order.")}
          </p>
        </motion.div>
      </header>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {SECTIONS.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.02, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`returns-section-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  § {s.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {s.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(s.body)}
                </p>
                {s.list && (
                  <ul className="mt-5 max-w-2xl border-l border-[#1a1a1a]">
                    {s.list.map(([label, value], idx) => (
                      <li
                        key={idx}
                        className="grid grid-cols-12 gap-3 pl-4 py-3 border-b border-[#141414] last:border-b-0"
                      >
                        <span className="col-span-12 md:col-span-4 font-mono-ui text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#7a7a7a]">
                          {cleanPolicyText(label)}
                        </span>
                        <span className="col-span-12 md:col-span-8 font-serif-editorial text-[14px] md:text-[16px] text-[#c9c9c4] leading-[1.7]">
                          {cleanPolicyText(value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.outro && (
                  <p className="font-serif-editorial text-[#c9c9c4] mt-5 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl italic">
                    {cleanPolicyText(s.outro)}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
            <p>Grievance Acknowledgement: Within 48 hours</p>
            <p>Resolution Timeline: Within 30 days</p>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="returns-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link
              to="/about"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              About
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/shipping"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Shipping
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/terms"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Terms
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/privacy"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Privacy
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <a
              href="https://www.instagram.com/theblackhouse.tbh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Instagram
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/the-black-house/"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Linkedin
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
